/**
 * textDeconcatenator.ts
 * 
 * Utilidad para detectar y corregir texto concatenado proveniente de 
 * parsers ODT/DOCX que fusionan celdas de tablas sin espacios.
 * 
 * ENFOQUE CONSERVADOR:
 * - Solo aplica patrones de ALTA CONFIANZA
 * - Usa diccionario PTEL para ALL CAPS concatenados
 * - Marca casos sospechosos para revisión manual
 * - Protege palabras completas y nombres propios españoles
 * 
 * Validado con datos reales de 6 documentos PTEL:
 * - Colomera, Quéntar, Hornos, Castril, Tíjola, Berja
 * - 865 registros, 377 con concatenaciones
 * 
 * CHANGELOG v2.13 (15-Abr-2026):
 * - FIX BUG-13: "AYUNTAMIE NTO" → "ayuntamiento" (ayuntamiento-v3, v4)
 * - FIX BUG-14: "IND USTRIAS DEL SECTOR QUÍMICO" → "Industrias del Sector Químico"
 *   Nuevos patrones: industrias, industrias-v2, industrias-v3
 * - TEST: 70/70 tests pasando (0 fallidos)
 *
 * CHANGELOG v2.12 (20-Feb-2026):
 * - NEW: PATRÓN 10b - Per-word ALL CAPS splitting (splitAllCapsPerWord)
 *   Procesa cada palabra ALL CAPS ≥6 chars individualmente con diccionario
 *   Resuelve "P. DEPANADERIAFRANCISCOSANCHEZ", "EDIFICIO DE LASEGURIDAD SOCIAL"
 * - NEW: restoreAccents() - Restauración de acentos post-split ALL CAPS
 *   "PANADERIA" → "PANADERÍA", "JOAQUIN" → "JOAQUÍN"
 * - FIX: Casos Berja #17, #30, #152 ahora se procesan correctamente
 * - TEST: 66/66 tests pasando (0 fallidos)
 *
 * CHANGELOG v2.11 (19-Ene-2026):
 * - FIX: Capitalización RAE incompleta en nombres compuestos
 *   "c/ virgen del carmen" → "c/ Virgen del Carmen" (antes solo "Virgen")
 * - NEW: PATRÓN P1-NORM-2b - Capitalización tras preposiciones (del/de la/de los/de las)
 * - TEST: 60/60 tests pasando (0 fallidos)
 *
 * CHANGELOG v2.10 (18-Ene-2026):
 * - FIX: BUG crítico - Patrones preposiciones fragmentaban palabras reconstruidas
 *   "Manuel García" → "Manu el García" ahora se mantiene correcto
 * - NEW: PROTECTED_FROM_PREP_SPLIT - Set autogenerado de palabras protegidas
 * - NEW: isInsideProtectedWord() - Verificación antes de fragmentar
 * - MOD: PATRÓN 5 y 6 ahora usan callback con verificación de palabras protegidas
 *
 * CHANGELOG v2.9 (18-Ene-2026):
 * - NEW: Patrón 11 - Reconstrucción de palabras fragmentadas ("poli de portiva" → "polideportiva")
 * - NEW: Patrón 12 - Capitalización post-abreviatura según RAE ("C/ amargura" → "C/ Amargura")
 * - NEW: reconstructFragmentedWords() para unir palabras incorrectamente separadas
 * - NEW: FRAGMENTED_WORDS_PATTERNS con 40+ patrones comunes en PTEL
 * - NEW: VIA_ABBREVIATIONS para capitalización RAE
 * - FIX: BUG-COLOMERA "Pi la r" → "Pilar", "poli de portiva" → "polideportiva"
 *
 * CHANGELOG v2.8 (08-Dic-2025):
 * - REFACTOR: Integración con TextLearningStore para datos dinámicos
 * - DEL: Eliminadas constantes hardcodeadas (FALSE_POSITIVES, DICCIONARIO_PTEL, etc.)
 * - ADD: Corrección ortográfica dinámica desde IndexedDB
 * - Sistema ahora aprende y persiste nuevos patrones
 * 
 * CHANGELOG v2.7 (08-Dic-2025):
 * - FIX: BUG-2 - Mejorado patrón preposiciones para "Sierrade Castril"
 * - FIX: BUG-3 - Protección de abreviaturas E.S., P.N., etc.
 * - ADD: PROTECTED_ABBREVIATIONS para evitar romper abreviaturas comunes
 * 
 * CHANGELOG v2.6 (30-Nov-2025):
 * - NEW: Diccionario PTEL con 150+ palabras de infraestructuras
 * - NEW: Patrón 10 - ALL CAPS con diccionario (CENTROSALUD → CENTRO SALUD)
 * - NEW: splitAllCapsWithDictionary() para separación inteligente
 * - TEST: 19/19 casos ALL CAPS (100% efectividad)
 * 
 * CHANGELOG v2.5 (30-Nov-2025):
 * - ADD: Nombres propios españoles a FALSE_POSITIVES
 * - FIX: Evita romper "Manuel Díaz" → "Manu el Díaz"
 * 
 * @version 2.12.0
 * @date 2026-02-20
 */

import {
  isFalsePositive,
  getDictionarySortedByLength,
  getProtectedAbbreviations,
  getAllCorrections,
} from './TextLearningStore';
import { esPalabraConocida } from './nombreCaja';

// ============================================================================
// TIPOS
// ============================================================================

export interface DeconcatenationResult {
  original: string;
  corrected: string;
  wasModified: boolean;
  patternsApplied: string[];
  confidence: 'ALTA' | 'MEDIA' | 'BAJA';
  warnings: string[];
  requiresReview: boolean;
}

export interface CoordinateValidation {
  original: number;
  corrected: number;
  wasCorrected: boolean;
  issue: 'truncated_y' | 'concatenated' | 'outlier' | null;
  warning: string | null;
}

// ============================================================================
// CONSTANTES LOCALES
// ============================================================================

/**
 * Preposiciones españolas que pueden aparecer concatenadas
 */
const PREPOSITIONS = ['de', 'del', 'la', 'el', 'los', 'las', 'al'];

/**
 * PATRÓN P1-NORM-1: Palabras fragmentadas conocidas
 * 
 * Corrige palabras que el parser ODT ha separado incorrectamente
 * debido a soft hyphens, zero-width spaces, o errores de encoding.
 * 
 * Formato: { pattern: RegExp, replacement: string, name: string }
 * - pattern: Regex que detecta la fragmentación (case-insensitive)
 * - replacement: Palabra correcta
 * - name: Identificador para logging
 * 
 * @version 1.0.0 (18-Ene-2026)
 */
const FRAGMENTED_WORDS_PATTERNS: Array<{ pattern: RegExp; replacement: string; name: string }> = [
  // =========================================================================
  // INFRAESTRUCTURAS FRAGMENTADAS
  // Patrones detectan múltiples variantes de fragmentación por parser ODT
  // NOTA: \s+ = uno o más espacios, \s* = cero o más espacios
  // IMPORTANTE: Patrones más específicos van ANTES que los genéricos
  // =========================================================================
  { pattern: /poli\s+de\s*portiva/gi, replacement: 'polideportiva', name: 'polideportiva' },
  { pattern: /poli\s*de\s+portiva/gi, replacement: 'polideportiva', name: 'polideportiva-v2' },
  { pattern: /poli\s+deportiva/gi, replacement: 'polideportiva', name: 'polideportiva-v3' },
  // transformador - múltiples variantes (requiere al menos un espacio)
  { pattern: /trans\s+for\s*ma\s*dor/gi, replacement: 'transformador', name: 'transformador' },
  { pattern: /trans\s*for\s+ma\s*dor/gi, replacement: 'transformador', name: 'transformador-v2' },
  { pattern: /trans\s*for\s*ma\s+dor/gi, replacement: 'transformador', name: 'transformador-v3' },
  { pattern: /transfor\s+ma\s*dor/gi, replacement: 'transformador', name: 'transformador-v4' },
  { pattern: /transfor\s*ma\s+dor/gi, replacement: 'transformador', name: 'transformador-v5' },
  { pattern: /trans\s+formador/gi, replacement: 'transformador', name: 'transformador-v6' },
  // trasformador - variantes (requiere al menos un espacio)
  { pattern: /tras\s+for\s*ma\s*dor/gi, replacement: 'trasformador', name: 'trasformador' },
  { pattern: /tras\s*for\s+ma\s*dor/gi, replacement: 'trasformador', name: 'trasformador-v2' },
  { pattern: /tras\s*for\s*ma\s+dor/gi, replacement: 'trasformador', name: 'trasformador-v3' },
  // consultorio - variantes (requiere al menos un espacio)
  { pattern: /con\s+sul\s*to\s*rio/gi, replacement: 'consultorio', name: 'consultorio' },
  { pattern: /con\s*sul\s+to\s*rio/gi, replacement: 'consultorio', name: 'consultorio-v2' },
  { pattern: /con\s*sul\s*to\s+rio/gi, replacement: 'consultorio', name: 'consultorio-v3' },
  // ambulatorio - variantes (requiere al menos un espacio)
  { pattern: /am\s+bu\s*la\s*to\s*rio/gi, replacement: 'ambulatorio', name: 'ambulatorio' },
  { pattern: /am\s*bu\s+la\s*to\s*rio/gi, replacement: 'ambulatorio', name: 'ambulatorio-v2' },
  { pattern: /am\s*bu\s*la\s+to\s*rio/gi, replacement: 'ambulatorio', name: 'ambulatorio-v3' },
  { pattern: /ambu\s+la\s*torio/gi, replacement: 'ambulatorio', name: 'ambulatorio-v4' },
  // cementerio - variantes (requiere al menos un espacio)
  { pattern: /ce\s+men\s*te\s*rio/gi, replacement: 'cementerio', name: 'cementerio' },
  { pattern: /ce\s*men\s+te\s*rio/gi, replacement: 'cementerio', name: 'cementerio-v2' },
  { pattern: /ce\s*men\s*te\s+rio/gi, replacement: 'cementerio', name: 'cementerio-v3' },
  { pattern: /ayun\s+ta\s*mien\s*to/gi, replacement: 'ayuntamiento', name: 'ayuntamiento' },
  { pattern: /ayun\s*ta\s+mien\s*to/gi, replacement: 'ayuntamiento', name: 'ayuntamiento-v2' },
  // REJUNTE (25-sep-2026): las regex de BUG-13 («AYUNTAMIE NTO», Colomera) se retiran;
  // ese corte lo une ahora rejuntarFragmentosPorDiccionario().
  { pattern: /de\s+pó\s*si\s*to/gi, replacement: 'depósito', name: 'depósito' },
  { pattern: /de\s*pó\s+si\s*to/gi, replacement: 'depósito', name: 'depósito-v2' },
  { pattern: /de\s+po\s*si\s*to/gi, replacement: 'deposito', name: 'deposito' },
  { pattern: /de\s*po\s+si\s*to/gi, replacement: 'deposito', name: 'deposito-v2' },
  
  // =========================================================================
  // NOMBRES PROPIOS FRAGMENTADOS (muy común en parsers ODT)
  // Variantes: "Pi la r", "Pi lar", "Pil ar"
  // IMPORTANTE: Patrones más específicos (con \s+) van ANTES que los genéricos (\s*)
  // =========================================================================
  // Nombres con "Pilar" - múltiples variantes de fragmentación
  { pattern: /\bPi\s+la\s+r\b/gi, replacement: 'Pilar', name: 'Pilar' },
  { pattern: /\bPi\s+la\s*r\b/gi, replacement: 'Pilar', name: 'Pilar-v2' },
  { pattern: /\bPi\s+lar\b/gi, replacement: 'Pilar', name: 'Pilar-v3' },
  { pattern: /\bPil\s+ar\b/gi, replacement: 'Pilar', name: 'Pilar-v4' },
  
  // Manuel - múltiples variantes (de más fragmentada a menos)
  { pattern: /\bMa\s+nu\s+el\b/gi, replacement: 'Manuel', name: 'Manuel' },
  { pattern: /\bMa\s+nuel\b/gi, replacement: 'Manuel', name: 'Manuel-v2' },
  { pattern: /\bMan\s+uel\b/gi, replacement: 'Manuel', name: 'Manuel-v3' },
  { pattern: /\bManu\s+el\b/gi, replacement: 'Manuel', name: 'Manuel-v4' },
  
  // Miguel - múltiples variantes (de más fragmentada a menos)
  { pattern: /\bMi\s+gu\s+el\b/gi, replacement: 'Miguel', name: 'Miguel' },
  { pattern: /\bMi\s+guel\b/gi, replacement: 'Miguel', name: 'Miguel-v2' },
  { pattern: /\bMig\s+uel\b/gi, replacement: 'Miguel', name: 'Miguel-v3' },
  { pattern: /\bMigu\s+el\b/gi, replacement: 'Miguel', name: 'Miguel-v4' },
  
  // Otros nombres comunes fragmentados
  // NOTA: Usar \s+ para requerir al menos un espacio (evita fragmentar palabras correctas)
  { pattern: /\bRa\s+fa\s*el\b/gi, replacement: 'Rafael', name: 'Rafael' },
  { pattern: /\bRa\s*fa\s+el\b/gi, replacement: 'Rafael', name: 'Rafael-v2' },
  { pattern: /\bGa\s+bri\s*el\b/gi, replacement: 'Gabriel', name: 'Gabriel' },
  { pattern: /\bGa\s*bri\s+el\b/gi, replacement: 'Gabriel', name: 'Gabriel-v2' },
  { pattern: /\bDa\s+ni\s*el\b/gi, replacement: 'Daniel', name: 'Daniel' },
  { pattern: /\bDa\s*ni\s+el\b/gi, replacement: 'Daniel', name: 'Daniel-v2' },
  { pattern: /\bIs\s+ma\s*el\b/gi, replacement: 'Ismael', name: 'Ismael' },
  { pattern: /\bIs\s*ma\s+el\b/gi, replacement: 'Ismael', name: 'Ismael-v2' },
  { pattern: /\bCar\s+men\b/gi, replacement: 'Carmen', name: 'Carmen' },
  { pattern: /\bDo\s+lo\s*res\b/gi, replacement: 'Dolores', name: 'Dolores' },
  { pattern: /\bDo\s*lo\s+res\b/gi, replacement: 'Dolores', name: 'Dolores-v2' },
  { pattern: /\bMer\s+ce\s*des\b/gi, replacement: 'Mercedes', name: 'Mercedes' },
  { pattern: /\bMer\s*ce\s+des\b/gi, replacement: 'Mercedes', name: 'Mercedes-v2' },
  { pattern: /\bRo\s+sa\s*rio\b/gi, replacement: 'Rosario', name: 'Rosario' },
  { pattern: /\bRo\s*sa\s+rio\b/gi, replacement: 'Rosario', name: 'Rosario-v2' },
  { pattern: /\bAn\s+to\s*nio\b/gi, replacement: 'Antonio', name: 'Antonio' },
  { pattern: /\bAn\s*to\s+nio\b/gi, replacement: 'Antonio', name: 'Antonio-v2' },
  { pattern: /\bFran\s+cis\s*co\b/gi, replacement: 'Francisco', name: 'Francisco' },
  { pattern: /\bFran\s*cis\s+co\b/gi, replacement: 'Francisco', name: 'Francisco-v2' },
  
  // Apellidos comunes (requieren al menos un espacio)
  { pattern: /\bGar\s+cí\s*a\b/gi, replacement: 'García', name: 'García' },
  { pattern: /\bGar\s*cí\s+a\b/gi, replacement: 'García', name: 'García-v2' },
  { pattern: /\bGar\s+ci\s*a\b/gi, replacement: 'Garcia', name: 'Garcia' },
  { pattern: /\bGar\s*ci\s+a\b/gi, replacement: 'Garcia', name: 'Garcia-v2' },
  { pattern: /\bLó\s+pez\b/gi, replacement: 'López', name: 'López' },
  { pattern: /\bLo\s+pez\b/gi, replacement: 'Lopez', name: 'Lopez' },
  { pattern: /\bMar\s+tí\s*nez\b/gi, replacement: 'Martínez', name: 'Martínez' },
  { pattern: /\bMar\s*tí\s+nez\b/gi, replacement: 'Martínez', name: 'Martínez-v2' },
  { pattern: /\bMar\s+ti\s*nez\b/gi, replacement: 'Martinez', name: 'Martinez' },
  { pattern: /\bMar\s*ti\s+nez\b/gi, replacement: 'Martinez', name: 'Martinez-v2' },
  { pattern: /\bSán\s+chez\b/gi, replacement: 'Sánchez', name: 'Sánchez' },
  { pattern: /\bSan\s+chez\b/gi, replacement: 'Sanchez', name: 'Sanchez' },
  
  // =========================================================================
  // TOPÓNIMOS Y LUGARES
  // NOTA: Usar \s+ (uno o más espacios) para evitar coincidir con palabras correctas
  // =========================================================================
  { pattern: /\bGra\s+na\s*da\b/gi, replacement: 'Granada', name: 'Granada' },
  { pattern: /\bGra\s*na\s+da\b/gi, replacement: 'Granada', name: 'Granada-v2' },
  { pattern: /\bAl\s+me\s*rí\s*a\b/gi, replacement: 'Almería', name: 'Almería' },
  { pattern: /\bAl\s*me\s+rí\s*a\b/gi, replacement: 'Almería', name: 'Almería-v2' },
  { pattern: /\bmá\s+la\s*ga\b/gi, replacement: 'Málaga', name: 'Málaga' },
  { pattern: /\bmá\s*la\s+ga\b/gi, replacement: 'Málaga', name: 'Málaga-v2' },
  { pattern: /\bCór\s+do\s*ba\b/gi, replacement: 'Córdoba', name: 'Córdoba' },
  { pattern: /\bCór\s*do\s+ba\b/gi, replacement: 'Córdoba', name: 'Córdoba-v2' },
  { pattern: /\bSe\s+vi\s*lla\b/gi, replacement: 'Sevilla', name: 'Sevilla' },
  { pattern: /\bSe\s*vi\s+lla\b/gi, replacement: 'Sevilla', name: 'Sevilla-v2' },
  
  // =========================================================================
  // PALABRAS COMUNES EN DIRECCIONES
  // NOTA: Requieren al menos un espacio para evitar fragmentar palabras correctas
  // =========================================================================
  { pattern: /\bca\s+rre\s*te\s*ra\b/gi, replacement: 'carretera', name: 'carretera' },
  { pattern: /\bca\s*rre\s+te\s*ra\b/gi, replacement: 'carretera', name: 'carretera-v2' },
  { pattern: /\bave\s+ni\s*da\b/gi, replacement: 'avenida', name: 'avenida' },
  { pattern: /\bave\s*ni\s+da\b/gi, replacement: 'avenida', name: 'avenida-v2' },
  { pattern: /\bca\s+lle\b/gi, replacement: 'calle', name: 'calle' },
  { pattern: /\bpla\s+za\b/gi, replacement: 'plaza', name: 'plaza' },
  { pattern: /\bdes\s+cu\s*bier\s*ta\b/gi, replacement: 'descubierta', name: 'descubierta' },
  { pattern: /\bcu\s+bier\s*ta\b/gi, replacement: 'cubierta', name: 'cubierta' },
  { pattern: /\bcu\s*bier\s+ta\b/gi, replacement: 'cubierta', name: 'cubierta-v2' },
  { pattern: /\bem\s+bal\s*se\b/gi, replacement: 'embalse', name: 'embalse' },
  { pattern: /\bem\s*bal\s+se\b/gi, replacement: 'embalse', name: 'embalse-v2' },
  // REJUNTE (25-sep-2026): las regex de BUG-14 («IND USTRIAS», Colomera) se retiran;
  // ese corte lo une ahora rejuntarFragmentosPorDiccionario().
];

/**
 * REJUNTE (25-sep-2026): une dos tokens contiguos A B que el documento trae partidos
 * por un espacio real («ENTRO DE TRANSFORM ACION», «Sub Estación», «AYUNTAMIE NTO»)
 * cuando el pegado es palabra conocida del diccionario de caja (`esPalabraConocida`)
 * y alguno de los dos trozos no lo es. Sustituye a los parches por municipio (BUG-13/14).
 *
 * Regla medida sobre el corpus (2 368 filas × nombre/dirección/tipo, 3 uniones, 0 falsas):
 *   len(A) ≥ 3, len(B) ≥ 2, A no es vacía, A+B ∈ diccionario,
 *   y (A ∉ diccionario o (B ∉ diccionario y len(B) ≥ 3)).
 * Sin los umbrales aparecen «FUENTE S», «IGLESIA S», «C P», «P L».
 * Caja del pegado: A conserva la suya; B va en minúscula salvo que A sea versales.
 * Solo pares separados por espacios; no encadena tres piezas (no hay ninguna en el corpus).
 */
const VACIAS_REJUNTE = new Set(['DE', 'DEL', 'LA', 'EL', 'LOS', 'LAS', 'Y', 'E', 'O', 'U', 'A', 'AL', 'EN', 'CON', 'POR', 'PARA', 'SIN', 'SOBRE', 'ENTRE']);
const TOKEN_REJUNTE = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+/g;

function claveRejunte(token: string): string {
  return token.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
}

function cumpleRejunte(a: string, b: string): boolean {
  if (a.length < 3 || b.length < 2 || VACIAS_REJUNTE.has(claveRejunte(a))) return false;
  if (!esPalabraConocida(a + b)) return false;
  if (!esPalabraConocida(a)) return true;
  return !esPalabraConocida(b) && b.length >= 3;
}

export function rejuntarFragmentosPorDiccionario(text: string): { result: string; uniones: string[] } {
  const uniones: string[] = [];
  const toks = Array.from(text.matchAll(TOKEN_REJUNTE));
  let out = '';
  let pos = 0;
  for (let i = 0; i < toks.length - 1; i++) {
    const a = toks[i];
    const b = toks[i + 1];
    const ia = a.index ?? 0;
    const ib = b.index ?? 0;
    const entre = text.slice(ia + a[0].length, ib);
    if (!/^ +$/.test(entre) || !cumpleRejunte(a[0], b[0])) continue;
    const aVersales = a[0] === a[0].toUpperCase();
    const pegado = a[0] + (aVersales ? b[0].toUpperCase() : b[0].toLowerCase());
    uniones.push(`${a[0]} ${b[0]}→${pegado}`);
    out += text.slice(pos, ia) + pegado;
    pos = ib + b[0].length;
    i++; // B ya está pegada: no vuelve a ser A del par siguiente
  }
  return { result: out + text.slice(pos), uniones };
}

/**
 * PATRÓN P1-NORM-2: Abreviaturas de vía que requieren mayúscula después
 * Según normativa RAE, el nombre de la calle siempre va con mayúscula inicial.
 * 
 * @version 1.0.0 (18-Ene-2026)
 */
const VIA_ABBREVIATIONS = [
  'C/', 'c/',           // Calle
  'Avd.', 'avd.', 'Avda.', 'avda.', 'Av.', 'av.',  // Avenida
  'Ctra.', 'ctra.',     // Carretera
  'Pza.', 'pza.', 'Pl.', 'pl.',  // Plaza
  'Pje.', 'pje.',       // Pasaje
  'Cno.', 'cno.',       // Camino
  'Urb.', 'urb.',       // Urbanización
  'Pol.', 'pol.',       // Polígono
];

/**
 * Palabras protegidas que NO deben fragmentarse por patrones de preposiciones.
 * Generado automáticamente desde FRAGMENTED_WORDS_PATTERNS replacements.
 * 
 * FIX BUG: "Manuel García" → "Manu el García" (patrón prep fragmentaba "Manuel")
 * 
 * @version 1.0.0 (18-Ene-2026)
 */
const PROTECTED_FROM_PREP_SPLIT = new Set(
  FRAGMENTED_WORDS_PATTERNS.map(p => p.replacement.toLowerCase())
);

/**
 * Mapa de restauración de acentos para palabras ALL CAPS post-split.
 * Solo incluye palabras frecuentes en PTEL donde la variante sin acento
 * es la que aparece en los documentos ODT (acentos perdidos por el parser).
 * 
 * @version 1.0.0 (20-Feb-2026)
 */
const ACCENT_RESTORATION_MAP: Record<string, string> = {
  'PANADERIA': 'PANADERÍA',
  'CARNICERIA': 'CARNICERÍA',
  'JOAQUIN': 'JOAQUÍN',
  'ELECTRICA': 'ELÉCTRICA',
  'ELECTRICAS': 'ELÉCTRICAS',
  'ELECTRICO': 'ELÉCTRICO',
  'DEPOSITO': 'DEPÓSITO',
  'GARCIA': 'GARCÍA',
  'SANCHEZ': 'SÁNCHEZ',
  'LOPEZ': 'LÓPEZ',
  'MARTINEZ': 'MARTÍNEZ',
  'FERNANDEZ': 'FERNÁNDEZ',
  'GONZALEZ': 'GONZÁLEZ',
  'ESTACION': 'ESTACIÓN',
  'PROTECCION': 'PROTECCIÓN',
  'CLINICA': 'CLÍNICA',
  'MEDICO': 'MÉDICO',
  'MEDICA': 'MÉDICA',
  'PUBLICO': 'PÚBLICO',
  'PUBLICA': 'PÚBLICA',
  'GUARDERIA': 'GUARDERÍA',
  'PABELLON': 'PABELLÓN',
  'FUTBOL': 'FÚTBOL',
  'PADEL': 'PÁDEL',
  'JARDIN': 'JARDÍN',
  'ALMACEN': 'ALMACÉN',
  'POLICIA': 'POLICÍA',
  'AUTOBUS': 'AUTOBÚS',
  'ALMERIA': 'ALMERÍA',
  'MALAGA': 'MÁLAGA',
  'JAEN': 'JAÉN',
  'CORDOBA': 'CÓRDOBA',
  'CADIZ': 'CÁDIZ',
  'TENSION': 'TENSIÓN',
  'MARQUES': 'MARQUÉS',
};

// ============================================================================
// FUNCIÓN: SEPARAR ALL CAPS CON DICCIONARIO
// ============================================================================

/**
 * Separa texto ALL CAPS concatenado usando el diccionario PTEL dinámico.
 * Usa matching greedy con backtracking para maximizar cobertura.
 * 
 * @example
 * splitAllCapsWithDictionary("CENTROSALUD") → "CENTRO SALUD"
 * splitAllCapsWithDictionary("HERMANOSPEÑA") → "HERMANOS PEÑA"
 * splitAllCapsWithDictionary("LASEGURIDAD") → "LA SEGURIDAD"
 * splitAllCapsWithDictionary("CPALMERÍA") → "CP ALMERÍA"
 */
export function splitAllCapsWithDictionary(text: string): {
  result: string;
  wasModified: boolean;
  partsFound: string[];
} {
  // Solo procesar ALL CAPS puros sin espacios, mínimo 6 caracteres
  if (!/^[A-ZÁÉÍÓÚÜÑ]+$/.test(text) || text.length < 6) {
    return { result: text, wasModified: false, partsFound: [] };
  }
  
  // Obtener diccionario dinámico (ya ordenado por longitud descendente)
  const dictionary = getDictionarySortedByLength();
  
  // Intentar split con backtracking para maximizar cobertura
  const parts = splitWithBacktracking(text, dictionary, 0);
  
  // Solo modificar si encontramos al menos 2 partes
  if (parts.length < 2) {
    return { result: text, wasModified: false, partsFound: [] };
  }

  // NOMRAE (24-sep-2026): splitWithBacktracking devuelve el resto sin casar como
  // última pieza, y aceptarlo sin más rompía palabras enteras del corpus
  // ("ALCUDIA" → "AL CUDIA", "DEPORTIVA" → "DE PORTIVA", "CONSEJERIA" →
  // "CON SEJERIA", "ENDESA" → "EN DE SA": 96 filas de 2 368). Regla: se exige
  // cobertura total del diccionario, salvo un único resto final de ≥ 4 letras
  // cuando todas las piezas casadas tienen ≥ 4 letras ("JOAQUINBARAZAS" →
  // "JOAQUIN BARAZAS", "COLEGIOGRAENA" → "COLEGIO GRAENA").
  const sinCasar = parts.filter(p => !dictionary.includes(p));
  if (sinCasar.length > 0) {
    const restoFinalAceptable =
      sinCasar.length === 1 &&
      parts[parts.length - 1] === sinCasar[0] &&
      sinCasar[0].length >= 4 &&
      parts.slice(0, -1).every(p => p.length >= 4);
    if (!restoFinalAceptable) {
      return { result: text, wasModified: false, partsFound: [] };
    }
  }
  
  const result = parts.join(' ');
  return { 
    result, 
    wasModified: result !== text,
    partsFound: parts
  };
}

/**
 * Split recursivo con backtracking. Prueba cada prefijo que coincide
 * con el diccionario y elige la combinación que maximiza la cobertura
 * (más caracteres matcheados por el diccionario).
 * 
 * Resuelve: "LASEGURIDAD" → ["LA","SEGURIDAD"] en vez de ["LAS","EGURIDAD"]
 * porque SEGURIDAD está en el diccionario y EGURIDAD no.
 * 
 * @param text - Texto restante por separar
 * @param dictionary - Diccionario ordenado por longitud desc
 * @param depth - Profundidad de recursión (limit: 20)
 */
function splitWithBacktracking(text: string, dictionary: string[], depth: number): string[] {
  if (text.length === 0) return [];
  if (depth > 20) return [text]; // Límite de profundidad
  
  // Recopilar todos los prefijos que coinciden
  const matchingPrefixes: string[] = [];
  for (const word of dictionary) {
    if (text.startsWith(word)) {
      matchingPrefixes.push(word);
    }
  }
  
  if (matchingPrefixes.length === 0) {
    // Sin match → devolver el resto como una sola pieza
    return [text];
  }
  
  // Si solo hay un match posible, usarlo directamente
  if (matchingPrefixes.length === 1) {
    const prefix = matchingPrefixes[0];
    const rest = text.substring(prefix.length);
    if (rest.length === 0) return [prefix];
    return [prefix, ...splitWithBacktracking(rest, dictionary, depth + 1)];
  }
  
  // Múltiples prefijos → probar cada uno y elegir el mejor
  let bestParts: string[] | null = null;
  let bestScore = -1;
  
  for (const prefix of matchingPrefixes) {
    const rest = text.substring(prefix.length);
    const restParts = rest.length === 0 
      ? [] 
      : splitWithBacktracking(rest, dictionary, depth + 1);
    const candidate = [prefix, ...restParts];
    
    // Puntuar: contar chars cubiertos por el diccionario
    let score = prefix.length; // El prefijo siempre está en dict
    for (const part of restParts) {
      if (dictionary.includes(part)) score += part.length;
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestParts = candidate;
    }
  }
  
  return bestParts || [text];
}

/**
 * Restaura acentos en palabras ALL CAPS que los perdieron por el parser ODT.
 * Solo aplica a palabras conocidas del mapa ACCENT_RESTORATION_MAP.
 * 
 * @example
 * restoreAccents("PANADERIA FRANCISCO SANCHEZ") → "PANADERÍA FRANCISCO SÁNCHEZ"
 * restoreAccents("SEGURIDAD SOCIAL") → "SEGURIDAD SOCIAL" (sin cambio)
 */
export function restoreAccents(text: string): string {
  return text.replace(/[A-ZÁÉÍÓÚÜÑ]+/g, (word) => {
    return ACCENT_RESTORATION_MAP[word] || word;
  });
}

/**
 * Aplica splitAllCapsWithDictionary a cada palabra ALL CAPS ≥6 chars
 * dentro de un texto que puede contener espacios, puntuación, etc.
 * 
 * Esto extiende el Pattern 10 (que solo funcionaba con texto 100% ALL CAPS)
 * para procesar textos mixtos como:
 *   "P. DEPANADERIAFRANCISCOSANCHEZ" → "P. DE PANADERÍA FRANCISCO SÁNCHEZ"
 *   "EDIFICIO DE LASEGURIDAD SOCIAL" → "EDIFICIO DE LA SEGURIDAD SOCIAL"
 * 
 * @example
 * splitAllCapsPerWord("P. DEPANADERIAFRANCISCOSANCHEZ")
 * // → { result: "P. DE PANADERÍA FRANCISCO SÁNCHEZ", modified: true }
 */
export function splitAllCapsPerWord(text: string): {
  result: string;
  modified: boolean;
  patternsApplied: string[];
} {
  // Separar texto preservando separadores (espacios, puntuación, etc.)
  const tokens = text.split(/(\s+|[.,;:!?()—\-]+)/);
  let modified = false;
  const patterns: string[] = [];
  
  const processed = tokens.map(token => {
    // Solo procesar tokens ALL CAPS puros ≥ 6 caracteres
    if (/^[A-ZÁÉÍÓÚÜÑ]+$/.test(token) && token.length >= 6) {
      const splitResult = splitAllCapsWithDictionary(token);
      if (splitResult.wasModified) {
        modified = true;
        patterns.push('diccionario-allcaps-perword');
        // Restaurar acentos en las partes resultantes
        return restoreAccents(splitResult.result);
      }
    }
    return token;
  });
  
  return {
    result: processed.join(''),
    modified,
    patternsApplied: patterns,
  };
}

/**
 * Verifica si una palabra en el texto es una palabra protegida.
 * Usado para evitar que los patrones de preposiciones fragmenten palabras
 * que fueron reconstruidas o son nombres propios conocidos.
 * 
 * @param text - Texto completo
 * @param matchStart - Posición donde comienza la coincidencia
 * @param matchEnd - Posición donde termina la coincidencia
 * @returns true si la coincidencia está dentro de una palabra protegida
 */
function isInsideProtectedWord(text: string, matchStart: number, matchEnd: number): boolean {
  // Buscar límites de la palabra que contiene la coincidencia
  let wordStart = matchStart;
  let wordEnd = matchEnd;
  
  // Expandir hacia atrás hasta encontrar límite de palabra
  while (wordStart > 0 && /[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/i.test(text[wordStart - 1])) {
    wordStart--;
  }
  
  // Expandir hacia adelante hasta encontrar límite de palabra
  while (wordEnd < text.length && /[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/i.test(text[wordEnd])) {
    wordEnd++;
  }
  
  const word = text.substring(wordStart, wordEnd).toLowerCase();
  return PROTECTED_FROM_PREP_SPLIT.has(word);
}

/**
 * PREPROTA: letras de la misma palabra que preceden a `pos` (sin incluirla).
 * «Sierrade» con pos en la «a» → «Sierr»; «dela» con pos en la «e» → «d».
 */
function prefijoDePalabra(text: string, pos: number): string {
  let inicio = pos;
  while (inicio > 0 && /[a-záéíóúüñA-ZÁÉÍÓÚÜÑ]/.test(text[inicio - 1])) {
    inicio--;
  }
  return text.substring(inicio, pos);
}

/**
 * Verifica si una posición está dentro de una abreviatura protegida.
 * BUG-3 FIX: Detecta patrones tipo "X.Y." para no romperlos.
 * Ahora usa TextLearningStore dinámico.
 */
function isInsideProtectedAbbreviation(text: string, dotPosition: number): boolean {
  // Buscar hacia atrás desde el punto para ver si es parte de abreviatura
  // Patrón: 1-3 letras mayúsculas + punto, posiblemente repetido
  const beforeDot = text.substring(Math.max(0, dotPosition - 4), dotPosition + 1);
  
  // Verificar si es parte de patrón X. o X.Y.
  if (/^[A-ZÁÉÍÓÚÜÑ]{1,3}\.$/.test(beforeDot.slice(-2)) ||
      /^[A-ZÁÉÍÓÚÜÑ]{1,3}\.$/.test(beforeDot.slice(-3)) ||
      /^[A-ZÁÉÍÓÚÜÑ]{1,3}\.$/.test(beforeDot.slice(-4))) {
    
    // Verificar si después del punto viene otra letra que también es parte de abreviatura
    const afterDot = text.substring(dotPosition + 1, dotPosition + 4);
    if (/^[A-ZÁÉÍÓÚÜÑ]{1,2}\./.test(afterDot) || /^[A-ZÁÉÍÓÚÜÑ]{1,2}\s/.test(afterDot)) {
      return true;
    }
    
    // Obtener set de abreviaturas protegidas desde TextLearningStore
    const protectedAbbreviations = getProtectedAbbreviations();
    
    // Verificar si el patrón completo está en la lista de protegidas
    for (const abbr of protectedAbbreviations) {
      const abbrStart = dotPosition - abbr.length + 1;
      if (abbrStart >= 0) {
        const candidate = text.substring(abbrStart, dotPosition + 1);
        if (candidate.toUpperCase() === abbr.toUpperCase()) {
          return true;
        }
      }
    }
  }
  
  return false;
}

// ============================================================================
// FUNCIÓN PRINCIPAL DE DESCONCATENACIÓN
// ============================================================================

/**
 * Desconcatena texto de forma conservadora.
 * Solo aplica patrones de ALTA CONFIANZA.
 */
export function deconcatenateText(input: string): DeconcatenationResult {
  const result: DeconcatenationResult = {
    original: input,
    corrected: input,
    wasModified: false,
    patternsApplied: [],
    confidence: 'ALTA',
    warnings: [],
    requiresReview: false,
  };

  if (!input || typeof input !== 'string' || input.length < 3) {
    return result;
  }

  let text = input.trim();

  // =========================================================================
  // PATRÓN P1-NORM-1: RECONSTRUCCIÓN DE PALABRAS FRAGMENTADAS (PRIMERO)
  // Debe ejecutarse ANTES de otros patrones para evitar conflictos
  // NOTA: Se ejecuta ANTES de verificar falsos positivos porque los
  // fragmentos individuales ("trans", "for", "ma", "dor") no deben
  // bloquear la reconstrucción cuando el texto incluye palabras válidas
  // como "eléctrico" que están en FALSE_POSITIVES.
  // =========================================================================
  for (const { pattern, replacement, name } of FRAGMENTED_WORDS_PATTERNS) {
    const beforeFrag = text;
    text = text.replace(pattern, replacement);
    if (text !== beforeFrag) {
      result.patternsApplied.push(`fragmentado:${name}`);
    }
  }

  // REJUNTE: fragmentos reales del documento unidos por diccionario (en lugar de
  // regex por municipio). Va aquí, antes del cribado de falsos positivos (que
  // devuelve el texto intacto si nada ha disparado) y antes de PATRÓN 10.
  const rejunte = rejuntarFragmentosPorDiccionario(text);
  if (rejunte.uniones.length > 0) {
    text = rejunte.result;
    result.patternsApplied.push('fragmentado:diccionario');
  }

  // Verificar falsos positivos DESPUÉS de reconstrucción
  // Esto protege palabras completas de ser fragmentadas por patrones
  // posteriores (camelCase, preposiciones, etc.)
  const lowerText = text.toLowerCase();
  const words = lowerText.split(/\s+/);
  for (const word of words) {
    const cleanWord = word.replace(/[.,;:!?()]/g, '');
    if (isFalsePositive(cleanWord)) {
      // Si hay falso positivo Y no se aplicó reconstrucción, retornar
      // Si ya se aplicó reconstrucción, continuar pero con cuidado
      if (result.patternsApplied.length === 0) {
        return result;
      }
      // Si ya hubo reconstrucción, saltar los patrones de fragmentación
      // y ir directo al post-procesamiento
      break;
    }
  }

  // =========================================================================
  // PATRÓN 10: ALL CAPS con diccionario (v2.6) - APLICAR PRIMERO
  // =========================================================================
  // Solo si el texto es ALL CAPS puro sin espacios
  if (/^[A-ZÁÉÍÓÚÜÑ]+$/.test(text) && text.length >= 6) {
    const dictResult = splitAllCapsWithDictionary(text);
    if (dictResult.wasModified) {
      text = restoreAccents(dictResult.result);
      result.patternsApplied.push('diccionario-allcaps');
    }
  }

  // =========================================================================
  // PATRÓN 10b: Per-word ALL CAPS splitting (v2.12)
  // Procesa cada palabra ALL CAPS ≥6 chars individualmente.
  // Resuelve textos mixtos: "P. DEPANADERIAFRANCISCOSANCHEZ",
  // "EDIFICIO DE LASEGURIDAD SOCIAL"
  // =========================================================================
  if (!result.patternsApplied.includes('diccionario-allcaps')) {
    const perWordResult = splitAllCapsPerWord(text);
    if (perWordResult.modified) {
      text = perWordResult.result;
      result.patternsApplied.push(...perWordResult.patternsApplied);
    }
  }

  // PATRÓN 0: MAYÚSCULAS + Abreviatura (M.ª, Dª, D.)
  const beforeAbbr0 = text;
  text = text.replace(/([A-ZÁÉÍÓÚÜÑ])(M\.ª|M\.a|Mª|Dª|D\.)(?=[A-Za-z])/g, '$1 $2');
  if (text !== beforeAbbr0) {
    result.patternsApplied.push('mayúscula+abreviatura');
  }

  // PATRÓN 1: camelCase (minúscula + MAYÚSCULA)
  const beforeCamel = text;
  text = text.replace(/([a-záéíóúüñ])([A-ZÁÉÍÓÚÜÑ])/g, '$1 $2');
  if (text !== beforeCamel) {
    result.patternsApplied.push('camelCase');
  }

  // PATRÓN 2: Número + Mayúscula
  const beforeNum = text;
  text = text.replace(/(\d)([A-ZÁÉÍÓÚÜÑ])/g, '$1 $2');
  if (text !== beforeNum) {
    result.patternsApplied.push('número+mayúscula');
  }

  // PATRÓN 3: Letra + Número largo (4+ dígitos)
  const beforeLetterNum = text;
  text = text.replace(/([A-Za-záéíóúüñÁÉÍÓÚÜÑ])(\d{4,})/g, '$1 $2');
  if (text !== beforeLetterNum) {
    result.patternsApplied.push('letra+número');
  }

  // PATRÓN 4: Abreviatura M.ª seguida de nombre
  const beforeAbbr = text;
  text = text.replace(/(M\.ª|M\.a|Mª|Dª|D\.)([A-ZÁÉÍÓÚÜÑ])/g, '$1 $2');
  if (text !== beforeAbbr) {
    result.patternsApplied.push('abreviatura');
  }

  // PATRÓN 5: Preposición pegada al final de palabra (BUG-2 FIX)
  // Ahora detecta tanto "ade C" como "ade " (espacio después)
  // IMPORTANTE: Usar flag 'g' sin 'i' para que [A-ZÁÉÍÓÚÜÑ] solo coincida con MAYÚSCULAS
  // FIX v2.10: Proteger palabras reconstruidas (Manuel, Miguel, etc.)
  for (const prep of PREPOSITIONS) {
    // Caso 1: vocal + prep + espacio + mayúscula (original)
    const pattern1 = new RegExp(
      `([aeiouáéíóúAEIOUÁÉÍÓÚ])(${prep})\\s+([A-ZÁÉÍÓÚÜÑ])`,
      'g'
    );
    const beforePrep1 = text;
    // Usar replace con callback para verificar palabras protegidas
    text = text.replace(pattern1, (match, vocal, prepMatch, mayus, offset) => {
      // Verificar si estamos dentro de una palabra protegida
      if (isInsideProtectedWord(beforePrep1, offset, offset + vocal.length + prepMatch.length)) {
        return match; // No modificar
      }
      // PREPROTA (DOP-1 b): solo partir si lo que queda delante de la preposición
      // es palabra conocida («Sierra|de Castril», «María|del Buen Aire»); si no,
      // la preposición es el final de una palabra correcta del documento
      // («Escue|la Infantil», «Re|al Mentesa», «Se|de Alcudia») y se deja.
      if (!esPalabraConocida(prefijoDePalabra(beforePrep1, offset) + vocal)) {
        return match;
      }
      return `${vocal} ${prepMatch} ${mayus}`;
    });
    
    // Caso 2: vocal + prep + mayúscula (sin espacio)
    // Solo si la tercera letra es MAYÚSCULA real (indica inicio de palabra)
    const pattern2 = new RegExp(
      `([aeiouáéíóúAEIOUÁÉÍÓÚ])(${prep})([A-ZÁÉÍÓÚÜÑ])`,
      'g'
    );
    const beforePrep2 = text;
    text = text.replace(pattern2, (match, vocal, prepMatch, mayus, offset) => {
      // Verificar si estamos dentro de una palabra protegida
      if (isInsideProtectedWord(beforePrep2, offset, offset + vocal.length + prepMatch.length)) {
        return match; // No modificar
      }
      return `${vocal} ${prepMatch} ${mayus}`;
    });
    
    if (text !== beforePrep1 || text !== beforePrep2) {
      result.patternsApplied.push(`preposición:${prep}`);
    }
  }

  // PATRÓN 6: Vocal + preposición pegada (casos residuales)
  // Solo aplicar si no se detectó ya en patrón 5
  // IMPORTANTE: Sin flag 'i' para distinguir mayúsculas de minúsculas
  // FIX v2.10: Proteger palabras reconstruidas
  if (!result.patternsApplied.some(p => p.startsWith('preposición:'))) {
    const beforeVocalPrep = text;
    // Solo cuando hay MAYÚSCULA después (indica inicio de palabra nueva)
    text = text.replace(/([aeiouáéíóúAEIOUÁÉÍÓÚ])(de|del|la|el)([A-ZÁÉÍÓÚÜÑ])/g, 
      (match, vocal, prep, mayus, offset) => {
        if (isInsideProtectedWord(beforeVocalPrep, offset, offset + vocal.length + prep.length)) {
          return match;
        }
        return `${vocal} ${prep} ${mayus}`;
      }
    );
    // NOTA: NO aplicar aquí el segundo patrón porque fragmenta palabras protegidas
    // como "Manuel " → "Manu el ". El primer patrón (con mayúscula) es suficiente.
    if (text !== beforeVocalPrep) {
      result.patternsApplied.push('vocal+preposición');
    }
  }

  // PATRÓN 7: Punto pegado a siguiente palabra (BUG-3 FIX)
  // Ahora protege abreviaturas tipo E.S., P.N., etc.
  const beforeDot = text;
  // Usar replace con función para verificar cada match
  text = text.replace(/\.([A-ZÁÉÍÓÚÜÑ])/g, (match, letter, offset) => {
    // Si está dentro de una abreviatura protegida, no modificar
    if (isInsideProtectedAbbreviation(text, offset)) {
      return match;
    }
    return '. ' + letter;
  });
  if (text !== beforeDot) {
    result.patternsApplied.push('punto+mayúscula');
  }

  // PATRÓN 8: Unidades pegadas a números
  const beforeUnit = text;
  text = text.replace(/(\d)(Km|km|Kms|kms|m|ha)(?=\d|\s|$)/gi, '$1 $2');
  text = text.replace(/(Km|km|Kms|kms)(\d)/gi, '$1 $2');
  if (text !== beforeUnit) {
    result.patternsApplied.push('unidad+número');
  }

  // PATRÓN 9: ALL CAPS + número al final
  const beforeAllCaps = text;
  text = text.replace(/([A-ZÁÉÍÓÚÜÑ]{3,})(\d+)$/g, '$1 $2');
  if (text !== beforeAllCaps) {
    result.patternsApplied.push('allcaps+número');
  }

  // =========================================================================
  // POST-PROCESAMIENTO
  // =========================================================================

  // PATRÓN P1-NORM-2: Capitalización después de abreviaturas de vía (RAE)
  // "C/ pilar" → "C/ Pilar", "Avd. virgen" → "Avd. Virgen"
  for (const abbrev of VIA_ABBREVIATIONS) {
    // Escapar caracteres especiales de regex en la abreviatura
    const escapedAbbrev = abbrev.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Patrón: abreviatura + espacio(s) + letra minúscula
    const pattern = new RegExp(`(${escapedAbbrev})\\s+([a-záéíóúüñ])`, 'g');
    const beforeCap = text;
    text = text.replace(pattern, (match, abbr, firstLetter) => 
      `${abbr} ${firstLetter.toUpperCase()}`
    );
    if (text !== beforeCap) {
      result.patternsApplied.push('capitalización-RAE');
    }
  }

  // PATRÓN P1-NORM-2b: Capitalización después de preposiciones en nombres propios
  // "Virgen del carmen" → "Virgen del Carmen" (según RAE, nombres propios compuestos)
  // Solo aplica cuando ya se detectó contexto de dirección (capitalización-RAE)
  if (result.patternsApplied.includes('capitalización-RAE')) {
    const beforePropNoun = text;
    // Capitalizar palabra después de "del ", "de la ", "de los ", "de las "
    text = text.replace(/\b(del|de la|de los|de las)\s+([a-záéíóúüñ])/gi, 
      (match, prep, firstLetter) => `${prep} ${firstLetter.toUpperCase()}`
    );
    if (text !== beforePropNoun) {
      result.patternsApplied.push('capitalización-RAE-compuesto');
    }
  }

  // Corrección ortográfica estática (la más común)
  // Para correcciones dinámicas completas, usar applyTextCorrections()
  text = text.replace(/\bTrasformador\b/gi, 'Transformador');
  
  // Normalizar espacios múltiples
  text = text.replace(/\s+/g, ' ').trim();

  // DETECCIÓN DE CASOS SOSPECHOSOS
  if (/^[A-ZÁÉÍÓÚÜÑ\s\.\-]+$/.test(input)) {
    const longSequences = input.match(/[A-ZÁÉÍÓÚÜÑ]{12,}/g);
    if (longSequences && longSequences.length > 0) {
      // Si el diccionario no pudo separar, marcar para revisión
      if (!result.patternsApplied.includes('diccionario-allcaps')) {
        result.requiresReview = true;
        result.warnings.push(
          `Texto ALL CAPS no separado: "${longSequences.join('", "')}"`
        );
      }
    }
  }

  result.corrected = text;
  result.wasModified = text !== input;

  // Calcular nivel de confianza
  if (result.patternsApplied.length === 0) {
    result.confidence = 'ALTA';
  } else if (result.patternsApplied.length <= 2) {
    result.confidence = 'ALTA';
  } else if (result.patternsApplied.length <= 4) {
    result.confidence = 'MEDIA';
  } else {
    result.confidence = 'BAJA';
    result.warnings.push('Múltiples patrones aplicados - verificar resultado');
  }

  if (result.requiresReview) {
    result.confidence = 'MEDIA';
  }

  return result;
}

// ============================================================================
// VALIDACIÓN DE COORDENADAS
// ============================================================================

const ANDALUCIA_UTM = {
  X_MIN: 100000,
  X_MAX: 620000,
  Y_MIN: 3980000,
  Y_MAX: 4290000,
};

export function validateCoordinateY(value: number): CoordinateValidation {
  const result: CoordinateValidation = {
    original: value,
    corrected: value,
    wasCorrected: false,
    issue: null,
    warning: null,
  };

  if (value >= 40000 && value < 300000) {
    const corrected = value + 4000000;
    if (corrected >= ANDALUCIA_UTM.Y_MIN && corrected <= ANDALUCIA_UTM.Y_MAX) {
      result.corrected = corrected;
      result.wasCorrected = true;
      result.issue = 'truncated_y';
      result.warning = `Y truncada: ${value} → ${corrected} (añadido 4.000.000)`;
      return result;
    }
  }

  if (value < ANDALUCIA_UTM.Y_MIN || value > ANDALUCIA_UTM.Y_MAX) {
    const x10 = value * 10;
    if (x10 >= ANDALUCIA_UTM.Y_MIN && x10 <= ANDALUCIA_UTM.Y_MAX) {
      result.corrected = x10;
      result.wasCorrected = true;
      result.issue = 'truncated_y';
      result.warning = `Y truncada: ${value} → ${x10} (×10)`;
      return result;
    }
    result.issue = 'outlier';
    result.warning = `Y fuera de rango Andalucía: ${value}`;
  }

  return result;
}

export function validateCoordinateX(value: number): CoordinateValidation {
  const result: CoordinateValidation = {
    original: value,
    corrected: value,
    wasCorrected: false,
    issue: null,
    warning: null,
  };

  if (value > 1e9) {
    result.issue = 'concatenated';
    result.warning = `X concatenada: ${value} - requiere separación manual`;
    return result;
  }

  if (value < ANDALUCIA_UTM.X_MIN || value > ANDALUCIA_UTM.X_MAX) {
    result.issue = 'outlier';
    result.warning = `X fuera de rango Andalucía: ${value}`;
  }

  return result;
}

export function splitConcatenatedCoordinates(value: string): {
  values: number[];
  wasConcatenated: boolean;
  warning: string | null;
} {
  const numeric = value.replace(/[^\d]/g, '');
  
  if (numeric.length >= 12) {
    const mid = Math.floor(numeric.length / 2);
    const first = parseInt(numeric.substring(0, mid), 10);
    const second = parseInt(numeric.substring(mid), 10);
    
    const firstValidX = first >= 100000 && first <= 700000;
    const firstValidY = first >= 3900000 && first <= 4400000;
    const secondValidX = second >= 100000 && second <= 700000;
    const secondValidY = second >= 3900000 && second <= 4400000;
    
    if ((firstValidX && secondValidX) || (firstValidY && secondValidY) ||
        (firstValidX && secondValidY) || (firstValidY && secondValidX)) {
      return {
        values: [first, second],
        wasConcatenated: true,
        warning: `Coordenadas concatenadas: ${first} | ${second}`,
      };
    }
  }
  
  return {
    values: [parseFloat(value) || 0],
    wasConcatenated: false,
    warning: null,
  };
}

export function detectMisplacedContent(text: string): {
  hasMisplacedContent: boolean;
  contentType: 'personal' | 'horario' | 'otro' | null;
  warning: string | null;
} {
  const patterns = {
    personal: /\d+\s*(SARGENTO|CABO|OFICIAL|AGENTE|OPERARIO|FUNCIONARIO|PERSONA|CONDUCTOR|VIGILANTE)/i,
    horario: /(LABORABLES|FESTIVOS|HORARIO|LUNES|VIERNES|MAÑANA|TARDE|24\s*HORAS)/i,
  };

  for (const [contentType, pattern] of Object.entries(patterns)) {
    if (pattern.test(text)) {
      return {
        hasMisplacedContent: true,
        contentType: contentType as 'personal' | 'horario',
        warning: `Campo TIPO contiene información de ${contentType.toUpperCase()} - posible error de parser`,
      };
    }
  }

  return {
    hasMisplacedContent: false,
    contentType: null,
    warning: null,
  };
}

// ============================================================================
// CORRECCIONES DINÁMICAS (ASYNC)
// ============================================================================

/**
 * Aplica correcciones ortográficas dinámicas desde TextLearningStore.
 * Esta función es asíncrona porque accede a IndexedDB.
 * 
 * @param text - Texto a corregir
 * @returns Texto corregido y lista de correcciones aplicadas
 * 
 * @example
 * const { corrected, applied } = await applyTextCorrections("Trasformador eléctrico");
 * // corrected: "Transformador eléctrico"
 * // applied: [{ from: "Trasformador", to: "Transformador" }]
 */
export async function applyTextCorrections(text: string): Promise<{
  original: string;
  corrected: string;
  applied: Array<{ from: string; to: string }>;
}> {
  const corrections = await getAllCorrections();
  let corrected = text;
  const applied: Array<{ from: string; to: string }> = [];
  
  for (const correction of corrections) {
    const flags = correction.caseSensitive ? 'g' : 'gi';
    const pattern = new RegExp(`\\b${escapeRegex(correction.from)}\\b`, flags);
    
    if (pattern.test(corrected)) {
      corrected = corrected.replace(pattern, correction.to);
      applied.push({ from: correction.from, to: correction.to });
    }
  }
  
  return { original: text, corrected, applied };
}

/**
 * Escapa caracteres especiales para uso en RegExp
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default {
  deconcatenateText,
  splitAllCapsWithDictionary,
  splitAllCapsPerWord,
  restoreAccents,
  applyTextCorrections,
  validateCoordinateX,
  validateCoordinateY,
  splitConcatenatedCoordinates,
  detectMisplacedContent,
};
